// What the home screen reads off the person's menu: their dashboards in tile order, and
// which pages they hold. No React in here.
//
// The MENU decides what is offered. A dashboard or a page the person does not hold is
// never drawn, and a dashboard's name is dashLabel() of its DashTab, so this screen
// cannot disagree with the top bar or Access Management.
import type { MenuItem } from '../../stores/menuStore';
import { DASHBOARD_GROUPS, DASHBOARD_INFO, normalisePath } from '../../navigation/catalog';
import { DASHBOARD_HOME_PATH, allowedDashTabs, type DashTab } from '../../navigation/dashboards';
import { holdsDashboard, menuChildren, menuCodes, pagesUnder } from '../../navigation/menuTree';

/** A link by name: a card of "Needs attention" that has no number. */
export interface Shortcut {
  to: string;
  name: string;
}

// ── Dashboards ───────────────────────────────────────────────────────────────────

// The order of the tiles inside each subject. It is NOT the order of the old tab strip
// (ALL_DASH_TABS): tiles are read subject by subject, the working lists first.
const CARD_ORDER: readonly DashTab[] = [
  'Deployment', 'Hiring', 'Onboarding', 'Attrition', 'EWS', 'Uniform & Asset Management', 'Data Hygiene',
  'Attendance', 'Approvals', 'Mood',
  'Red Flag', 'Yellow Flag', 'Appeals',
  'Score Movement', 'Training',
];

/** Dashboards in tile order: by subject (DASHBOARD_GROUPS), then the agreed order inside it. */
export function orderDashTabs(tabs: readonly DashTab[]): DashTab[] {
  const groupAt = (t: DashTab) => DASHBOARD_GROUPS.findIndex((g) => g.id === DASHBOARD_INFO[t].group);
  return [...tabs].sort((a, b) => groupAt(a) - groupAt(b) || CARD_ORDER.indexOf(a) - CARD_ORDER.indexOf(b));
}

/**
 * The dashboards this person may open, in tile order. Nobody without the Dashboard
 * itself gets any; with it, the per-dashboard rule is the Dashboard's own (fail-open
 * for a role that predates the per-dashboard menus).
 *
 * One exception: a menu that arrived with no rows at all gets every dashboard. This
 * mirrors the old Dashboard page, which showed all its tabs to a role with no menu
 * ticked (the shell does not send that person away either). It is kept so nobody's
 * access changes in this release. `items` must be the menu once it has LOADED: before
 * that it is empty for everyone.
 */
export function homeDashTabs(items: MenuItem[]): DashTab[] {
  if (items.length > 0 && !holdsDashboard(items)) return [];
  return orderDashTabs(allowedDashTabs(menuCodes(items)));
}

// ── Pages ────────────────────────────────────────────────────────────────────────

/**
 * The row for an address the person holds, ignoring its query string; null when they
 * do not hold it. A section row can share its first page's address (My Approvals and
 * Approvals both sit at /approvals): the page is the one to name, so it wins.
 */
export function heldPage(pages: MenuItem[], path: string): MenuItem | null {
  const wanted = normalisePath(path);
  const rows = pages.filter((m) => m.path && normalisePath(m.path) === wanted);
  return rows.find((m) => !pages.some((p) => p.parent_id === m.id)) ?? rows[0] ?? null;
}

/**
 * True when the person holds a page other than the Dashboard: a top-level row that is a
 * page itself or has one under it. A heading with nothing to open does not count.
 */
export function holdsAnyPage(pages: MenuItem[]): boolean {
  return menuChildren(pages, null).some((top) => {
    if (top.path && normalisePath(top.path) === DASHBOARD_HOME_PATH) return false;
    return Boolean(top.path) || pagesUnder(pages, top).length > 0;
  });
}
